import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { trackEvent } from '../lib/analytics';

function getInitialSignupMode() {
  if (typeof window === 'undefined') return false;

  const params = new URLSearchParams(window.location.search);
  return params.get('mode') === 'signup';
}

function getCampaignAttribution() {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const attribution = {};

  const campaignFields = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];

  campaignFields.forEach((field) => {
    const value = params.get(field);

    if (value) {
      attribution[field] = value;
    }
  });

  return attribution;
}

function AuthScreen({ recoveryMode = false, onRecoveryComplete }) {
  const [isSignup, setIsSignup] = useState(getInitialSignupMode);
  const [isResetRequest, setIsResetRequest] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [authError, setAuthError] = useState('');
  const [authMessage, setAuthMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const [campaignAttribution] = useState(getCampaignAttribution);

  /*
    Track the initial authentication page landing.

    A URL such as:
    /?mode=signup&utm_source=kit&utm_medium=email&utm_campaign=launch_email_3

    will record that the user arrived directly in create-account mode
    from the launch email.
  */
  useEffect(() => {
    if (recoveryMode) return;

    trackEvent('auth_screen_opened', {
      mode: isSignup ? 'signup' : 'signin',
      ...campaignAttribution,
    });

    // This should run only for the initial auth-screen landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!recoveryMode) return;

    trackEvent('password_recovery_opened', {
      ...campaignAttribution,
    });
  }, [campaignAttribution, recoveryMode]);

  const ensureProfile = async (user) => {
    if (!user) return;

    const { error } = await supabase.from('profiles').upsert({
      id: user.id,
      email: user.email,
    });

    if (error) {
      console.error('Profile upsert error:', error.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setAuthError('');
    setAuthMessage('');
    setLoading(true);

    const authMode = isSignup ? 'signup' : 'signin';

    trackEvent(`${authMode}_submitted`, {
      ...campaignAttribution,
    });

    try {
      if (isSignup) {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) throw error;

        if (data.user) {
          await ensureProfile(data.user);
        }

        trackEvent('signup_succeeded', {
          email_confirmation_required: Boolean(
            data.user && !data.session
          ),
          ...campaignAttribution,
        });

        setAuthMessage(
          'Account created. If email confirmation is enabled, check your inbox before logging in.'
        );
      } else {
        const { data, error } =
          await supabase.auth.signInWithPassword({
            email,
            password,
          });

        if (error) throw error;

        if (data.user) {
          await ensureProfile(data.user);
        }

        trackEvent('signin_succeeded', {
          ...campaignAttribution,
        });
      }
    } catch (err) {
      console.error(`${authMode} error:`, err);

      trackEvent(`${authMode}_failed`, {
        error_code: err?.code || err?.name || 'unknown',
        ...campaignAttribution,
      });

      setAuthError(err.message || 'Authentication failed.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordResetEmail = async (e) => {
    e.preventDefault();

    setAuthError('');
    setAuthMessage('');

    if (!email) {
      setAuthError('Enter your email address first.');
      return;
    }

    setLoading(true);

    trackEvent('password_reset_submitted', {
      ...campaignAttribution,
    });

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(
        email,
        {
          redirectTo: window.location.origin,
        }
      );

      if (error) throw error;

      trackEvent('password_reset_succeeded', {
        ...campaignAttribution,
      });

      setAuthMessage(
        'Password reset email sent. Check your inbox and follow the link to set a new password.'
      );
    } catch (err) {
      console.error('Password reset error:', err);

      trackEvent('password_reset_failed', {
        error_code: err?.code || err?.name || 'unknown',
        ...campaignAttribution,
      });

      setAuthError(
        err.message || 'Unable to send password reset email.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    setAuthError('');
    setAuthMessage('');

    if (!newPassword || !confirmNewPassword) {
      setAuthError('Enter and confirm your new password.');
      return;
    }

    if (newPassword.length < 6) {
      setAuthError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setAuthError('Passwords do not match.');
      return;
    }

    setLoading(true);

    trackEvent('password_update_submitted', {
      ...campaignAttribution,
    });

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      trackEvent('password_update_succeeded', {
        ...campaignAttribution,
      });

      setAuthMessage(
        'Password updated successfully. Redirecting to your workspace...'
      );

      setNewPassword('');
      setConfirmNewPassword('');

      setTimeout(() => {
        if (onRecoveryComplete) {
          onRecoveryComplete();
        }
      }, 900);
    } catch (err) {
      console.error('Password update error:', err);

      trackEvent('password_update_failed', {
        error_code: err?.code || err?.name || 'unknown',
        ...campaignAttribution,
      });

      setAuthError(
        err.message || 'Unable to update password.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAuthModeSwitch = () => {
    const nextIsSignup = !isSignup;

    setIsSignup(nextIsSignup);
    setAuthError('');
    setAuthMessage('');
    setIsResetRequest(false);

    trackEvent(
      nextIsSignup ? 'signup_viewed' : 'signin_viewed',
      {
        source: 'auth_mode_switch',
        ...campaignAttribution,
      }
    );
  };

  const handleOpenPasswordReset = () => {
    setIsResetRequest(true);
    setAuthError('');
    setAuthMessage('');

    trackEvent('password_reset_viewed', {
      ...campaignAttribution,
    });
  };

  const handleReturnToSignin = () => {
    setIsResetRequest(false);
    setIsSignup(false);
    setAuthError('');
    setAuthMessage('');

    trackEvent('signin_viewed', {
      source: 'password_reset_back',
      ...campaignAttribution,
    });
  };

  if (recoveryMode) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1 className="auth-title">Reset Password</h1>

          <p className="auth-subtitle">
            Create a new password for your account.
          </p>

          <form
            className="auth-form"
            onSubmit={handleUpdatePassword}
          >
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={6}
            />

            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmNewPassword}
              onChange={(e) =>
                setConfirmNewPassword(e.target.value)
              }
              required
              minLength={6}
            />

            <button type="submit" disabled={loading}>
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>

          {authError && <p className="error">{authError}</p>}

          {authMessage && (
            <p className="success-message">{authMessage}</p>
          )}
        </div>
      </div>
    );
  }

  if (isResetRequest) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1 className="auth-title">Reset Password</h1>

          <p className="auth-subtitle">
            Enter your email and we’ll send you a password reset
            link.
          </p>

          <form
            className="auth-form"
            onSubmit={handlePasswordResetEmail}
          >
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send reset link'}
            </button>
          </form>

          {authError && <p className="error">{authError}</p>}

          {authMessage && (
            <p className="success-message">{authMessage}</p>
          )}

          <button
            type="button"
            className="auth-switch"
            onClick={handleReturnToSignin}
          >
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h1 className="auth-title">StockVested</h1>

        <p className="auth-subtitle">
          {isSignup
            ? 'Create an account to start your free trial.'
            : 'Sign in to access your private analysis workspace.'}
        </p>

        <form className="auth-form" onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
          />

          {!isSignup && (
            <button
              type="button"
              className="auth-forgot"
              onClick={handleOpenPasswordReset}
            >
              Forgot password?
            </button>
          )}

          <button type="submit" disabled={loading}>
            {loading
              ? 'Please wait...'
              : isSignup
              ? 'Create account'
              : 'Sign in'}
          </button>
        </form>

        {authError && <p className="error">{authError}</p>}

        {authMessage && (
          <p className="success-message">{authMessage}</p>
        )}

        <button
          type="button"
          className="auth-switch"
          onClick={handleAuthModeSwitch}
        >
          {isSignup
            ? 'Already have an account? Sign in'
            : 'Need an account? Create one'}
        </button>
      </div>
    </div>
  );
}

export default AuthScreen;